import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DIMS_SALES = [["outlet","Outlet"],["month","Month"],["day","Day"],["channel","Channel"]];
const DIMS_EXP   = [["outlet","Outlet"],["month","Month"],["day","Day"],["category","Category"],["kind","Fixed/Variable"]];
const METRICS_SALES = [["amount","Sales ₹"],["bills","Bills"],["avg_bill","Avg bill ₹"],["count","Row count"]];
const METRICS_EXP   = [["amount","Expense ₹"],["count","Row count"]];

/**
 * Universal pivot builder. Reset row/col/metric to valid defaults when source
 * changes (previously produced blank/invalid selects — see iteration_12 report).
 */
export default function PivotBuilder() {
  const [source, setSource] = useState("sales");
  const [row, setRow] = useState("outlet");
  const [col, setCol] = useState("month");
  const [metric, setMetric] = useState("amount");
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get("/ops/pivot/summary").then(r => setSummary(r.data)).catch(() => {}); }, []);

  const DIMS    = source === "sales" ? DIMS_SALES   : DIMS_EXP;
  const METRICS = source === "sales" ? METRICS_SALES : METRICS_EXP;

  const changeSource = (next) => {
    setSource(next);
    // Reset row/col/metric to valid values for the new source
    setRow("outlet");
    setCol(next === "sales" ? "month" : "category");
    setMetric("amount");
    setResult(null);
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ops/pivot", {
        source, row, col: col || null, metric,
        date_from: df || null, date_to: dt || null,
      });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
          <SumTile label="Sales rows"    value={summary.sales_records} />
          <SumTile label="Expense rows"  value={summary.expense_records} />
          <SumTile label="Sales total"   value={`₹${summary.sales_total.toLocaleString()}`} />
          <SumTile label="Expense total" value={`₹${summary.expense_total.toLocaleString()}`} />
          <SumTile label="Gross est."    value={`₹${summary.gross_profit_est.toLocaleString()}`} />
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center text-sm">
        <select data-testid="pv-source" value={source} onChange={e => changeSource(e.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}}>
          <option value="sales">Sales</option><option value="expenses">Expenses</option>
        </select>
        <span style={{color:"#7a6349"}}>Row</span>
        <select data-testid="pv-row" value={row} onChange={e => setRow(e.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}}>
          {DIMS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span style={{color:"#7a6349"}}>× Col</span>
        <select data-testid="pv-col" value={col} onChange={e => setCol(e.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}}>
          <option value="">— none —</option>
          {DIMS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span style={{color:"#7a6349"}}>= Metric</span>
        <select data-testid="pv-metric" value={metric} onChange={e => setMetric(e.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}}>
          {METRICS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="pv-df" type="date" value={df} onChange={e => setDf(e.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
        <input data-testid="pv-dt" type="date" value={dt} onChange={e => setDt(e.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
        <Button data-testid="pv-run" size="sm" onClick={run} disabled={busy} className="rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722] disabled:opacity-60">
          {busy ? "…" : "Run"}
        </Button>
      </div>
      {result && (
        <div className="rounded-xl border bg-white p-2 overflow-x-auto" style={{ borderColor:"#E4D6A8" }} data-testid="pv-result">
          <table className="w-full text-sm">
            <thead className="bg-[#F5EFE0] text-[10px] uppercase font-mono2 tracking-widest" style={{color:"#7a6349"}}>
              <tr><th className="px-3 py-2 text-left">{row}</th>
                {result.cols.map(c => <th key={c} className="px-3 py-2 text-right">{c}</th>)}
                <th className="px-3 py-2 text-right">Total</th></tr>
            </thead>
            <tbody>
              {result.rows.map((r,i) => (
                <tr key={r} className="border-t" style={{borderColor:"#F3E7C4"}}>
                  <td className="px-3 py-2 font-medium">{r}</td>
                  {result.cols.map((c,j) => <td key={c} className="px-3 py-2 text-right">{result.table[i][j].toLocaleString()}</td>)}
                  <td className="px-3 py-2 text-right font-bold">{result.row_totals[i].toLocaleString()}</td>
                </tr>
              ))}
              <tr className="border-t-2 font-bold" style={{borderColor:"#B4841F", background:"#FFF8E7"}}>
                <td className="px-3 py-2">Total</td>
                {result.col_totals.map((v,j) => <td key={j} className="px-3 py-2 text-right">{v.toLocaleString()}</td>)}
                <td className="px-3 py-2 text-right">{result.grand_total.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
          <div className="text-[10px] font-mono2 uppercase tracking-widest mt-2" style={{color:"#7a6349"}}>{result.record_count} records · metric: {result.metric} · source: {result.source}</div>
        </div>
      )}
    </div>
  );
}

function SumTile({ label, value }) {
  return (
    <div className="rounded-lg p-2 bg-white border" style={{ borderColor: "#E4D6A8" }}>
      <div className="text-[10px] uppercase font-mono2 tracking-widest" style={{color:"#7a6349"}}>{label}</div>
      <b>{value}</b>
    </div>
  );
}
